/**
 * Event Type Constants
 *
 * Centralized event naming conventions for the EventBus system.
 * All events follow the pattern: domain:resource:action
 *
 * Examples:
 * - connection:server:opened
 * - robot:123:status
 * - webrtc:ice:candidate
 */

// ============== Server Connection Events ==============

export const CONNECTION_EVENTS = {
  // Server connection state changes
  SERVER_STATE_CHANGED: 'connection:server:state',
  SERVER_OPENED: 'connection:server:opened',
  SERVER_CLOSED: 'connection:server:closed',
  SERVER_ERROR: 'connection:server:error',
  SERVER_CONNECTING: 'connection:server:connecting',
  SERVER_AUTHENTICATED: 'connection:server:authenticated',
  SERVER_RECONNECTING: 'connection:server:reconnecting',

  // Message events
  MESSAGE_RECEIVED: 'connection:server:message',
  MESSAGE_SENT: 'connection:server:sent',
  COMMAND_RESPONSE: 'connection:server:command:response',
  COMMAND_TIMEOUT: 'connection:server:command:timeout',
};

// ============== Robot Connection Events ==============

/**
 * Get robot-specific event name
 * @param {string} robotId - Robot identifier
 * @param {string} event - Event name
 * @returns {string} Namespaced event name
 */
export const getRobotEvent = (robotId, event) => `robot:${robotId}:${event}`;

export const nomalizeRobotTopicName = (topic) => {
  // Remove leading/trailing slashes and replace inner slashes with colons
  // return topic.trim().replace(/^\/|\/$/g, '').replace(/\//g, ':');
  return topic.trim().replace(/^\/+|\/+$/g, '').replace(/\//g, ':');
}

export const ROBOT_EVENTS = {
  // Robot connection state
  CONNECTION_STATE: 'connection:state',
  CONNECTION_OPENED: 'connection:opened',
  CONNECTION_CLOSED: 'connection:closed',
  CONNECTION_FAILED: 'connection:failed',
  CONNECTION_CONNECTING: 'connection:connecting',

  // Robot status
  STATUS_UPDATED: 'robot.status',
  ONLINE_STATUS_CHANGED: 'online:status',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',

  // Signaling
  SIGNAL_SDP: 'signal:sdp',
  SIGNAL_CANDIDATE: 'signal:candidate',
  SIGNAL_OFFER: 'signal:offer',
  SIGNAL_ANSWER: 'signal:answer',
  SIGNALING_READY: 'signaling:ready',

  // Media
  MEDIA_TRACK_ADDED: 'media:track:added',
  MEDIA_TRACK_REMOVED: 'media:track:removed',
  MEDIA_STATE_CHANGED: 'media:state',

  // Data channel
  CHANNEL_OPENED: 'channel:opened',
  CHANNEL_CLOSED: 'channel:closed',
  CHANNEL_MESSAGE: 'channel:message',
  CHANNEL_ERROR: 'channel:error',

  // Peer events
  PEERS_UPDATED: 'peers:updated',
  P2P_EVENT: 'p2p:event',
};

// ============== WebRTC Events ==============

export const WEBRTC_EVENTS = {
  // ICE events
  ICE_CANDIDATE: 'webrtc:ice:candidate',
  ICE_STATE_CHANGED: 'webrtc:ice:state',
  ICE_GATHERING_COMPLETE: 'webrtc:ice:complete',

  // Connection state
  CONNECTION_STATE_CHANGED: 'webrtc:connection:state',
  SIGNALING_STATE_CHANGED: 'webrtc:signaling:state',

  // Statistics
  STATS_UPDATED: 'webrtc:stats:updated',
  STATS_ERROR: 'webrtc:stats:error',

  // Errors
  ERROR: 'webrtc:error',
  MEDIA_ERROR: 'webrtc:media:error',
};

// ============== Message Events ==============

export const MESSAGE_EVENTS = {
  // Robot status messages
  ROBOT_STATUS: 'message:robot_status',
  ROBOT_STATUS_PARSED: 'message:robot_status:parsed',

  // Command messages
  CMD_VEL: 'message:cmd_vel',
  JOY: 'message:joy',
  KEYBOARD: 'message:keyboard',

  // Mission messages
  MISSION_STATUS: 'message:mission_status',
  MISSION_UPDATE: 'message:mission_update',

  // Validation
  VALIDATION_ERROR: 'message:validation:error',
  PARSE_ERROR: 'message:parse:error',
};

// ============== Error Events ==============

export const ERROR_TYPES = {
  // Connection errors
  NETWORK: 'error:network',
  TIMEOUT: 'error:timeout',
  AUTH: 'error:auth',
  PROTOCOL: 'error:protocol',

  // WebRTC errors
  WEBRTC_MEDIA: 'error:webrtc:media',
  WEBRTC_ICE: 'error:webrtc:ice',
  WEBRTC_SIGNALING: 'error:webrtc:signaling',
  WEBRTC_DATA_CHANNEL: 'error:webrtc:data_channel',

  // Message errors
  MESSAGE_VALIDATION: 'error:message:validation',
  MESSAGE_PARSE: 'error:message:parse',
  MESSAGE_TIMEOUT: 'error:message:timeout',

  // General errors
  UNKNOWN: 'error:unknown',
};

// ============== Utility Functions ==============

/**
 * Create a robot-scoped event bus namespace
 * @param {string} robotId - Robot identifier
 * @returns {string} Namespace string
 */
export const createRobotNamespace = (robotId) => `robot:${robotId}`;

/**
 * Parse event name into components
 * @param {string} eventName - Full event name
 * @returns {Object} Parsed components { domain, resource, action }
 */
export const parseEventName = (eventName) => {
  const parts = eventName.split(':');
  return {
    domain: parts[0] || null,
    resource: parts[1] || null,
    action: parts.slice(2).join(':') || null,
    full: eventName
  };
};

/**
 * Check if event is a robot event
 * @param {string} eventName - Event name to check
 * @returns {boolean} True if robot event
 */
export const isRobotEvent = (eventName) => {
  return eventName.startsWith('robot:');
};

/**
 * Extract robot ID from robot event name
 * @param {string} eventName - Robot event name
 * @returns {string|null} Robot ID or null
 */
export const extractRobotId = (eventName) => {
  if (!isRobotEvent(eventName)) {
    return null;
  }
  const parts = eventName.split(':');
  return parts[1] || null;
};

// ============== Wildcard Patterns ==============

export const EVENT_PATTERNS = {
  // All robot events
  ALL_ROBOTS: 'robot:*',
  ALL_ROBOT_STATUS: 'robot:*:status',
  ALL_ROBOT_CONNECTIONS: 'robot:*:connection:*',
  ALL_ROBOT_SIGNALS: 'robot:*:signal:*',

  // All connection events
  ALL_CONNECTIONS: 'connection:*',
  ALL_CONNECTION_STATES: '*:connection:state',

  // All errors
  ALL_ERRORS: 'error:*',
  ALL_WEBRTC_ERRORS: 'error:webrtc:*',

  // All messages
  ALL_MESSAGES: 'message:*',
};

// Export all as single object for convenience
export default {
  CONNECTION_EVENTS,
  ROBOT_EVENTS,
  WEBRTC_EVENTS,
  MESSAGE_EVENTS,
  ERROR_TYPES,
  EVENT_PATTERNS,
  getRobotEvent,
  createRobotNamespace,
  parseEventName,
  isRobotEvent,
  extractRobotId,
};
